import React, { useState } from "react";
import { toast } from "sonner";
import { X, Trash2, ArrowRight, ShoppingBag, Minus, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEnquiry } from "@/context/EnquiryContext";

export default function EnquiryDrawer() {
  const { items, setQty, remove, clear, totalUnits, open, setOpen } = useEnquiry();
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (totalUnits === 0) {
      toast.error("Add at least one pair to your enquiry.");
      return;
    }
    if (!buyer.name || !/\S+@\S+\.\S+/.test(buyer.email)) {
      toast.error("Please add your name and a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          buyer,
          lines: items.map((i) => ({
            style: i.styleName,
            colour: i.colourName,
            strength: i.strength,
            qty: i.qty,
          })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Enquiry failed");
      }
      toast.success("Enquiry sent. We'll be in touch shortly.");
      clear();
      setBuyer({ name: "", email: "", phone: "", notes: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-[color:var(--ge-navy)]/40 backdrop-blur-[2px]"
            data-testid="enquiry-backdrop"
          />
          {/* panel */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 z-[70] h-full w-full sm:w-[440px] md:w-[520px] bg-white shadow-2xl flex flex-col"
            data-testid="enquiry-drawer"
            role="dialog"
            aria-label="Enquiry cart"
          >
            {/* header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--ge-navy)]/10 bg-[color:var(--ge-cream)]">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-full" style={{ background: "var(--ge-navy)", color: "#fff" }}>
                  <ShoppingBag size={16} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/60">Your Enquiry</div>
                  <div className="text-lg font-bold text-[color:var(--ge-navy)]">{totalUnits} pair{totalUnits === 1 ? "" : "s"}</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="enquiry-close"
                aria-label="Close enquiry"
                className="rounded-full p-2 hover:bg-white/60 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* items */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-[color:var(--ge-navy)]/70 py-24">
                  <ShoppingBag size={38} className="mb-4 opacity-60" />
                  <div className="text-sm uppercase tracking-[0.24em] font-semibold">Your enquiry is empty</div>
                  <p className="mt-2 text-sm max-w-xs">
                    Add a pair from any product page and we&apos;ll send your selection to sales@goodeyes.co.nz.
                  </p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((it, idx) => (
                    <li key={`${it.styleSlug}-${it.colourSlug}-${it.strength}`} className="flex gap-4 items-center border-b border-[color:var(--ge-navy)]/10 pb-4 last:border-b-0" data-testid={`enquiry-line-${it.styleSlug}-${it.colourSlug}-${it.strength.replace(".","-")}`}>
                      <img src={it.img} alt={it.colourName} className="h-16 w-24 object-contain shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[color:var(--ge-navy)]">{it.styleName}</div>
                        <div className="text-xs text-[color:var(--ge-navy)]/70 flex items-center gap-2">
                          <span>{it.colourName}</span>
                          <span className="inline-block w-1 h-1 rounded-full bg-[color:var(--ge-navy)]/40" />
                          <span>Strength {it.strength}</span>
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1 border border-[color:var(--ge-navy)]/15 rounded-full overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setQty(idx, it.qty - 1)}
                            aria-label="Decrease quantity"
                            data-testid={`enquiry-qty-dec-${idx}`}
                            className="h-8 w-8 flex items-center justify-center hover:bg-[color:var(--ge-cream)] transition-colors"
                          >
                            <Minus size={12} />
                          </button>
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={it.qty}
                            onChange={(e) => setQty(idx, e.target.value)}
                            data-testid={`enquiry-qty-${idx}`}
                            className="w-10 text-center bg-transparent text-sm font-bold text-[color:var(--ge-navy)] focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setQty(idx, it.qty + 1)}
                            aria-label="Increase quantity"
                            data-testid={`enquiry-qty-inc-${idx}`}
                            className="h-8 w-8 flex items-center justify-center hover:bg-[color:var(--ge-cream)] transition-colors"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(idx)}
                        aria-label="Remove item"
                        data-testid={`enquiry-remove-${idx}`}
                        className="text-[color:var(--ge-navy)]/60 hover:text-[color:var(--ge-red)] transition-colors self-start"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* buyer + submit */}
            {items.length > 0 && (
              <form onSubmit={submit} className="border-t border-[color:var(--ge-navy)]/10 px-6 py-5 bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="Name*" value={buyer.name} onChange={(v) => setBuyer({ ...buyer, name: v })} tid="enquiry-buyer-name" />
                  <Input label="Email*" value={buyer.email} onChange={(v) => setBuyer({ ...buyer, email: v })} tid="enquiry-buyer-email" />
                  <Input label="Phone" value={buyer.phone} onChange={(v) => setBuyer({ ...buyer, phone: v })} tid="enquiry-buyer-phone" />
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/70">Notes</label>
                    <textarea
                      rows={2}
                      value={buyer.notes}
                      onChange={(e) => setBuyer({ ...buyer, notes: e.target.value })}
                      data-testid="enquiry-buyer-notes"
                      className="mt-1 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-3 py-2 text-sm text-[color:var(--ge-navy)] resize-y"
                      placeholder="Anything we should know?"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="enquiry-submit"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ge-navy)] text-white px-5 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-red)] transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : `Send enquiry (${totalUnits})`} <ArrowRight size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={clear}
                    data-testid="enquiry-clear"
                    className="inline-flex items-center gap-1 rounded-full border border-[color:var(--ge-navy)]/20 px-4 py-3.5 text-[11px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/80 hover:bg-[color:var(--ge-cream)] transition-colors"
                  >
                    <Trash2 size={12} /> Clear
                  </button>
                </div>
                <p className="mt-3 text-[10px] text-[color:var(--ge-navy)]/60 leading-relaxed">
                  Submitting sends your enquiry to sales@goodeyes.co.nz. We&apos;ll reply within one business day with pricing and next steps.
                </p>
              </form>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

const Input = ({ label, value, onChange, tid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/70">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={tid}
      className="mt-1 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-3 py-2 text-sm text-[color:var(--ge-navy)]"
    />
  </div>
);
